import { useState } from 'react';
import { color, publishTheme, studyStatusTheme } from '../styles/theme';
import { useStudies, useCreateStudy, type Study } from '../api/studies';
import { useAuth } from '../auth/AuthContext';
import { PageTitle, Card, ColHead, Button, Meter, Loading, ErrorNote } from '../components/ui';
import { IconMail, IconPlus } from '../components/icons';
import { StudyEditor } from './StudyEditor';

const GRID = '142px minmax(0,2.2fr) minmax(0,1.35fr) 0.8fr minmax(110px,1fr) 64px';

/** What a new study is called until someone gives it a title. */
const UNTITLED = 'Untitled study';
const UNTITLED_SLUG = 'untitled-study';

export function Studies() {
  const studies = useStudies();
  const create = useCreateStudy();
  const { can } = useAuth();
  const [editSlug, setEditSlug] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);

  const manage = can('studies:manage');

  if (studies.isLoading) return <Loading />;
  if (studies.error) return <ErrorNote message={(studies.error as Error).message} />;

  const rows: Study[] = studies.data ?? [];
  const editing = rows.find((s) => s.slug === editSlug);

  if (editing) {
    return (
      <StudyEditor
        study={editing}
        others={rows.filter((s) => s.slug !== editing.slug)}
        canManage={manage}
        showPreview={preview}
        onTogglePreview={() => setPreview((v) => !v)}
        onBack={() => {
          setEditSlug(null);
          setPreview(false);
        }}
      />
    );
  }

  /**
   * A blank draft, opened straight into the editor. The API wants a title and
   * makes the study's address from it, and the address never changes after,
   * so a second untitled draft gets the next free one rather than a 409.
   */
  async function addStudy() {
    const taken = new Set(rows.map((s) => s.slug));
    let slug = UNTITLED_SLUG;
    for (let n = 2; taken.has(slug); n++) slug = `${UNTITLED_SLUG}-${n}`;
    const created = await create.mutateAsync({
      label: UNTITLED,
      slug,
      status: 'development',
      published: false,
      cta: 'intake',
      target: 0,
      enrolled: 0,
      questions: [],
    });
    setEditSlug(created.study.slug);
    setPreview(false);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }} className="fade-in">
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <PageTitle
            sub={`${rows.length} ${rows.length === 1 ? 'study' : 'studies'} — create, edit and publish research studies on the site.`}
          >
            Studies
          </PageTitle>
        </div>
        {manage && (
          <Button variant="navy" onClick={() => void addStudy()} disabled={create.isPending}>
            <IconPlus /> {create.isPending ? 'Creating…' : 'New study'}
          </Button>
        )}
      </div>

      {create.error && <ErrorNote message={(create.error as Error).message} />}

      <Card pad={0} style={{ overflow: 'hidden' }}>
        <div className="x-scroll">
          <div style={{ minWidth: 760 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: GRID,
                gap: 12,
                padding: '11px 22px',
                background: color.bg,
              }}
            >
              <ColHead>Code</ColHead>
              <ColHead>Study</ColHead>
              <ColHead>Status</ColHead>
              <ColHead>Visibility</ColHead>
              <ColHead>Enrollment</ColHead>
              <span />
            </div>

            {rows.map((s) => {
              const st = studyStatusTheme[s.status];
              const pub = publishTheme[s.published ? 'published' : 'draft'];
              const pct = s.target > 0 ? Math.min(100, Math.round((s.enrolled / s.target) * 100)) : 0;
              const open = () => {
                setEditSlug(s.slug);
                setPreview(false);
              };

              return (
                <div
                  key={s.slug}
                  className="hov-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: GRID,
                    gap: 12,
                    alignItems: 'center',
                    padding: '14px 22px',
                    borderBottom: `1px solid ${color.line}`,
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    {s.code ? (
                      <span
                        title="The lab's protocol code"
                        style={{
                          display: 'inline-block',
                          maxWidth: '100%',
                          fontSize: 11.5,
                          fontWeight: 800,
                          letterSpacing: '0.04em',
                          padding: '4px 9px',
                          borderRadius: 7,
                          background: color.tint,
                          color: color.blueDeep,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {s.code}
                      </span>
                    ) : (
                      /* No protocol code yet: the slug is the identifier the
                         working study list uses, so show that rather than a
                         dash — it is what the team reads from today. */
                      <span
                        title={`Slug — ${s.slug}. Add a protocol code in the editor to show that instead.`}
                        style={{
                          display: 'inline-block',
                          maxWidth: '100%',
                          fontSize: 11.5,
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          color: color.muted,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {s.slug}
                      </span>
                    )}
                  </span>

                  <button
                    onClick={open}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        display: 'block',
                        fontSize: 14,
                        fontWeight: 700,
                        color: color.ink,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {s.label || 'Untitled study'}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11.5,
                        color: color.muted,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {s.tags ||
                        `${s.questions.length} screening question${s.questions.length === 1 ? '' : 's'}`}
                    </span>
                    {s.mailbox && (
                      <span
                        title="Staff replies from this study’s records go out from this mailbox."
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                          marginTop: 3,
                          fontSize: 11.5,
                          fontWeight: 600,
                          color: color.blueDeep,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <IconMail size={12} />
                        {s.mailbox}
                      </span>
                    )}
                  </button>

                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        maxWidth: '100%',
                        fontSize: 11,
                        fontWeight: 800,
                        padding: '5px 12px',
                        borderRadius: 999,
                        background: st.bg,
                        color: st.fg,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {st.label}
                    </span>
                  </span>

                  <span>
                    <span
                      style={{
                        display: 'inline-block',
                        fontSize: 11,
                        fontWeight: 800,
                        padding: '5px 12px',
                        borderRadius: 999,
                        background: pub.bg,
                        color: pub.fg,
                      }}
                    >
                      {pub.label}
                    </span>
                  </span>

                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 12,
                        fontWeight: 700,
                        color: color.ink,
                        fontVariantNumeric: 'tabular-nums',
                        marginBottom: 5,
                      }}
                    >
                      {s.enrolled}/{s.target} enrolled
                    </span>
                    <Meter pct={pct} height={6} />
                  </span>

                  <Button variant="ghost" onClick={open} style={{ padding: '6px 14px', fontSize: 12 }}>
                    {manage ? 'Edit' : 'View'}
                  </Button>
                </div>
              );
            })}

            {rows.length === 0 && (
              <p style={{ padding: '28px 22px', fontSize: 14, color: color.muted, margin: 0 }}>
                No studies yet.
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
